import type { WorkbookDefinition } from '@erp/shared-types';
import { replaceEqualDeep } from '@tanstack/react-query';
import {
  processOutline,
  type ProcessOutline,
  type ProcessStep,
} from '@erp/workbooks';

function changedOrderIds(before: readonly string[], after: readonly string[]) {
  const afterPosition = new Map(after.map((id, index) => [id, index]));
  const shared = before.flatMap((id) => {
    const position = afterPosition.get(id);
    return position === undefined ? [] : [{ id, position }];
  });
  const changed = new Set<string>();
  shared.forEach((left, first) => {
    for (const right of shared.slice(first + 1)) {
      if (left.position <= right.position) continue;
      changed.add(left.id);
      changed.add(right.id);
    }
  });
  return changed;
}

/** A move swaps siblings, so only order among siblings counts as a reorder. */
function reorderedSiblingIds(
  next: WorkbookDefinition,
  before: ProcessOutline,
  after: ProcessOutline,
) {
  const changed = new Set<string>();
  const ids = (steps: readonly ProcessStep[]) => steps.map((step) => step.id);
  for (const parent of new Set(next.processSteps.map((step) => step.parentId)))
    for (const id of changedOrderIds(
      ids(before.children(parent)),
      ids(after.children(parent)),
    ))
      changed.add(id);
  return changed;
}

function reviewStatus(
  instructionEdited: boolean,
  configurationEdited: boolean,
) {
  if (instructionEdited && configurationEdited) return 'both-edited';
  if (instructionEdited) return 'instruction-edited';
  if (configurationEdited) return 'configuration-edited';
  return undefined;
}

/**
 * Structurally shares `next` with `previous`: every subtree of `next` that deep-equals
 * the one in `previous` is replaced by it. After this, identity means equality, so
 * change tracking and the no-op check never walk unchanged items.
 */
export function shareUnchanged(
  previous: WorkbookDefinition,
  next: WorkbookDefinition,
): WorkbookDefinition {
  return replaceEqualDeep(previous, next);
}

// Identity is the fast path; only an item whose reference changed is compared deeply.
function sameItem(before: unknown, after: unknown) {
  return before === after || replaceEqualDeep(before, after) === before;
}

function sameIds(before: readonly string[], after: readonly string[]) {
  return (
    before === after ||
    (before.length === after.length &&
      before.every((id, index) => id === after[index]))
  );
}

// Track intent/configuration drift at the edit boundary so it survives tab changes
// and autosave. Only an explicit successful generation (or an accepted SOP
// update) clears the review marker; a restored step (an undo) keeps the marker
// it is written with. Steps whose review status is unchanged keep their
// identity, and so does `next` when no status changed.
export function trackProcessChanges(
  previous: WorkbookDefinition,
  next: WorkbookDefinition,
  appliedStepIds: readonly string[] = [],
  restoredStepIds: readonly string[] = [],
): WorkbookDefinition {
  const changedItems = new Set<string>();
  const beforeItems = new Map<string, unknown>(
    [
      ...previous.files,
      ...previous.sources,
      ...previous.sheets,
      ...previous.outputs,
    ].map((item) => [item.id, item]),
  );
  const afterItems = new Map<string, unknown>(
    [...next.files, ...next.sources, ...next.sheets, ...next.outputs].map(
      (item) => [item.id, item],
    ),
  );
  for (const id of new Set([...beforeItems.keys(), ...afterItems.keys()])) {
    if (!sameItem(beforeItems.get(id), afterItems.get(id)))
      changedItems.add(id);
  }
  for (const [before, after] of [
    [previous.sources, next.sources],
    [previous.sheets, next.sheets],
  ] as const) {
    if (before === after) continue;
    for (const id of changedOrderIds(
      before.map((item) => item.id),
      after.map((item) => item.id),
    ))
      changedItems.add(id);
  }
  const previousSteps = new Map(
    previous.processSteps.map((step) => [step.id, step]),
  );
  const previousOutline = processOutline(previous);
  const nextOutline = processOutline(next);
  const reorderedSteps = reorderedSiblingIds(
    next,
    previousOutline,
    nextOutline,
  );
  let statusChanged = false;
  const processSteps = next.processSteps.map((step): ProcessStep => {
    if (restoredStepIds.includes(step.id)) return step;
    const status = appliedStepIds.includes(step.id)
      ? undefined
      : nextReviewStatus(step);
    if (status === step.reviewStatus) return step;
    statusChanged = true;
    const { reviewStatus: _ignored, ...content } = step;
    return status ? { ...content, reviewStatus: status } : content;
  });
  return statusChanged ? { ...next, processSteps } : next;

  function nextReviewStatus(step: ProcessStep) {
    const before = previousSteps.get(step.id);
    const linkedIds = new Set([
      ...nextOutline.linkedItemIds(step.id),
      ...previousOutline.linkedItemIds(step.id),
    ]);
    return reviewStatus(
      instructionEdited(before, step),
      configurationEdited(before, step, reorderedSteps) ||
        [...linkedIds].some((id) => changedItems.has(id)),
    );
  }
}

function wasEdited(
  before: ProcessStep,
  marker: 'instruction-edited' | 'configuration-edited',
) {
  return (
    before.reviewStatus === marker || before.reviewStatus === 'both-edited'
  );
}

/** A new step, or one whose instructions changed (or already carried the marker). */
function instructionEdited(before: ProcessStep | undefined, step: ProcessStep) {
  if (!before) return true;
  return (
    wasEdited(before, 'instruction-edited') ||
    before.instructions !== step.instructions
  );
}

/** A step moved, re-parented, or re-linked (or already carrying the marker). */
function configurationEdited(
  before: ProcessStep | undefined,
  step: ProcessStep,
  reorderedSteps: ReadonlySet<string>,
) {
  if (!before) return false;
  return (
    wasEdited(before, 'configuration-edited') ||
    before.parentId !== step.parentId ||
    !sameIds(before.itemIds, step.itemIds) ||
    reorderedSteps.has(step.id)
  );
}
